import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import PageHeader from 'tio-ui/components/page-header';
import Paginator from 'tio-ui/components/paginator';
import PeopleFilterRail from 'tio-common/components/people/filter-rail';
import PersonCard from 'tio-common/components/people/person-card';
import SearchInput from 'tio-ui/components/search-input';
import Text from 'tio-ui/components/text';
import { PhUsers, PhX } from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { csvToKeys, csvWithout, facetLabel, withCount } from 'tio-common/utils/people-facet-labels';
import type {
  EmployeeIndexModel,
  EmployeeRow,
} from 'tio-employee/routes/authenticated/admin/employees/index';
import type EmployeesIndexController from 'tio-employee/controllers/authenticated/admin/employees/index';
import type { PeopleFacetGroup } from 'tio-common/components/people/filter-rail';
import type { PersonCardPerson } from 'tio-common/components/people/person-card';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router';

/** Detail route the person cards link to. Partner admins see the employee record, not the person. */
const DETAIL_ROUTE = 'authenticated.admin.employees.show';

interface ActiveFilterChip {
  key: string;
  label: string;
  remove: () => void;
}

export interface S {
  Args: {
    controller: EmployeesIndexController;
    model: EmployeeIndexModel;
  };
}

export default class EmployeeIndexRoute extends Component<S> {
  @service declare intl: IntlService;
  @service declare router: RouterService;

  // Any search/filter change resets to page 1 so the user isn't stranded on an
  // out-of-range page (which would show an empty list even with matches).
  didSearch = (value: string) => {
    this.args.controller.search = value;
    this.args.controller.page = 1;
  };

  onStatus = (csv: string) => {
    this.args.controller.status = csv;
    this.args.controller.page = 1;
  };

  onEnrollment = (csv: string) => {
    this.args.controller.enrollment = csv;
    this.args.controller.page = 1;
  };

  onParticipation = (csv: string) => {
    this.args.controller.participationStatus = csv;
    this.args.controller.page = 1;
  };

  onJurisdictions = (csv: string) => {
    this.args.controller.jurisdictions = csv;
    this.args.controller.page = 1;
  };

  clearAllFilters = () => {
    this.args.controller.search = '';
    this.args.controller.status = '';
    this.args.controller.enrollment = '';
    this.args.controller.participationStatus = '';
    this.args.controller.jurisdictions = '';
    this.args.controller.page = 1;
  };

  createEmployeeTransition = () => {
    this.router.transitionTo('authenticated.admin.employees.add');
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = page;
  };

  get hasAnyFilter(): boolean {
    const c = this.args.controller;
    return Boolean(
      c.search || c.status || c.enrollment || c.participationStatus || c.jurisdictions
    );
  }

  get items() {
    return this.args.model?.items ?? [];
  }

  get total(): number {
    return this.args.model?.total ?? 0;
  }

  get facets() {
    return this.args.model?.facets;
  }

  /**
   * The rail's four groups, in display order. Each label carries its bucket
   * count; the Roles group renders only when the result set holds a
   * partner-visible role, since the rail hides empty groups.
   */
  get facetGroups(): PeopleFacetGroup[] {
    const facets = this.facets;
    const status = facets?.status ?? {};
    const participation = facets?.participation ?? {};
    const enrollment = facets?.enrollment ?? {};
    const c = this.args.controller;

    if (!facets) return [];

    return [
      {
        key: 'status',
        label: this.intl.t('people.facet_employee_status'),
        items: [
          { key: 'active', label: withCount(this.intl.t('people.status_active'), status.active) },
          {
            key: 'inactive',
            label: withCount(this.intl.t('people.status_inactive'), status.inactive),
          },
        ],
        selectedKeys: csvToKeys(c.status),
        onChange: (keys: string[]) => this.onStatus(keys.join(',')),
      },
      {
        key: 'participation',
        label: this.intl.t('people.facet_participation'),
        items: [
          {
            key: 'participating',
            label: withCount(
              this.intl.t('people.participation_participating'),
              participation.participating
            ),
          },
          {
            key: 'pending',
            label: withCount(this.intl.t('people.participation_pending'), participation.pending),
          },
          {
            key: 'deactivated',
            label: withCount(
              this.intl.t('people.participation_deactivated'),
              participation.deactivated
            ),
          },
        ],
        selectedKeys: csvToKeys(c.participationStatus),
        onChange: (keys: string[]) => this.onParticipation(keys.join(',')),
      },
      {
        // Overlapping buckets by design (ever ⊇ active) — mirrors tio-admin.
        key: 'enrollment',
        label: this.intl.t('people.facet_enrollment'),
        items: [
          {
            key: 'activeEnrolled',
            label: withCount(
              this.intl.t('people.enrollment_active_enrolled'),
              enrollment.activeEnrolled
            ),
          },
          {
            key: 'everEnrolled',
            label: withCount(
              this.intl.t('people.enrollment_ever_enrolled'),
              enrollment.everEnrolled
            ),
          },
          {
            key: 'neverEnrolled',
            label: withCount(this.intl.t('people.enrollment_never'), enrollment.neverEnrolled),
          },
        ],
        selectedKeys: csvToKeys(c.enrollment),
        onChange: (keys: string[]) => this.onEnrollment(keys.join(',')),
      },
      {
        key: 'roles',
        label: this.intl.t('people.filter_roles'),
        items: (facets.roles ?? []).map((bucket) => ({
          key: bucket.key,
          label: withCount(
            facetLabel(this.intl, `people.jurisdictions.${bucket.key}`, bucket.key),
            bucket.count
          ),
        })),
        selectedKeys: csvToKeys(c.jurisdictions),
        onChange: (keys: string[]) => this.onJurisdictions(keys.join(',')),
      },
    ];
  }

  /**
   * Adapt each projected row to the shared card's view model. A partner admin
   * sees exactly one employment — their own company's — so `employments` always
   * holds a single entry and carries no company name.
   */
  get cards(): PersonCardPerson[] {
    return this.items.map((row: EmployeeRow) => ({
      id: row.id,
      firstName: row.firstName,
      lastName: row.lastName,
      isActiveEmployee: row.isActiveEmployee,
      isEnrolled: row.isEnrolled,
      isRegistered: row.isRegistered,
      roleLabels: row.jurisdictions.map((role) =>
        facetLabel(this.intl, `people.jurisdictions.${role}`, role)
      ),
      ...(row.participationStatus && {
        participationLabel: facetLabel(
          this.intl,
          `people.participation_${row.participationStatus.toLowerCase()}`,
          row.participationStatus
        ),
      }),
      ...(row.matchedQueries && { matchedQueries: row.matchedQueries }),
      employments: [
        {
          ...(row.payrollId && { payrollId: row.payrollId }),
          ...(row.email && { email: row.email }),
          ...(row.createdAt && { createdAt: row.createdAt }),
        },
      ],
    }));
  }

  // Removable chips for every facet selection (minus the search box, which is
  // its own visible control): employee status, participation, enrollment, roles.
  get activeFilterChips(): ActiveFilterChip[] {
    const c = this.args.controller;
    const chips: ActiveFilterChip[] = [];

    const statusLabels: Record<string, string> = {
      active: this.intl.t('people.status_active'),
      inactive: this.intl.t('people.status_inactive'),
    };
    for (const key of csvToKeys(c.status)) {
      chips.push({
        key: `status-${key}`,
        label: statusLabels[key] ?? key,
        remove: () => this.onStatus(csvWithout(c.status, key)),
      });
    }

    const participationLabels: Record<string, string> = {
      participating: this.intl.t('people.participation_participating'),
      pending: this.intl.t('people.participation_pending'),
      deactivated: this.intl.t('people.participation_deactivated'),
    };
    for (const key of csvToKeys(c.participationStatus)) {
      chips.push({
        key: `participation-${key}`,
        label: participationLabels[key] ?? key,
        remove: () => this.onParticipation(csvWithout(c.participationStatus, key)),
      });
    }

    const enrollmentLabels: Record<string, string> = {
      activeEnrolled: this.intl.t('people.enrollment_active_enrolled'),
      everEnrolled: this.intl.t('people.enrollment_ever_enrolled'),
      neverEnrolled: this.intl.t('people.enrollment_never'),
    };
    for (const key of csvToKeys(c.enrollment)) {
      chips.push({
        key: `enrollment-${key}`,
        label: enrollmentLabels[key] ?? key,
        remove: () => this.onEnrollment(csvWithout(c.enrollment, key)),
      });
    }

    for (const name of csvToKeys(c.jurisdictions)) {
      chips.push({
        key: `role-${name}`,
        label: facetLabel(this.intl, `people.jurisdictions.${name}`, name),
        remove: () => this.onJurisdictions(csvWithout(c.jurisdictions, name)),
      });
    }

    return chips;
  }

  get showPaginator(): boolean {
    return this.total > Number(this.args.controller.limit);
  }

  // A name search whose whole result set is fuzzy. Read from the exact-match
  // aggregation rather than the current page: an exact `should` clause raises a
  // hit's score without guaranteeing it outranks every fuzzy match, so scanning
  // page one would claim "no exact matches" while one sat on page two.
  get showNoExactNameNotice(): boolean {
    return this.args.model?.exactNameMatches === 0 && this.total > 0;
  }

  <template>
    {{pageTitle (t "partner.employees")}}

    <PageHeader @title={{t "partner.employees"}} @icon={{PhUsers}}>
      <:breadcrumbs as |b|>
        <b.crumb @label="Admin" />
        <b.crumb @label="Employees" />
      </:breadcrumbs>
      <:toolbar>
        <SearchInput
          @label={{t "partner.employee.search"}}
          @placeholder={{t "people.search_placeholder"}}
          @value={{@controller.search}}
          @onChange={{this.didSearch}}
          @onSearch={{this.didSearch}}
        />
      </:toolbar>
      <:actions>
        <Button
          @intent="primary"
          @appearance="outlined"
          {{on "click" this.createEmployeeTransition}}
        >
          {{t "partner.employee.create_employee"}}
        </Button>
      </:actions>
    </PageHeader>

    <div class="grid grid-cols-1 lg:grid-cols-4 items-start gap-6 pt-4">
      <PeopleFilterRail
        class="lg:col-span-1"
        @label={{t "people.filters"}}
        @groups={{this.facetGroups}}
        @hasActiveFilters={{this.hasAnyFilter}}
        @clearLabel={{t "people.clear_all_filters"}}
        @onClearAll={{this.clearAllFilters}}
      />

      <div class="lg:col-span-3 min-w-0">
        <div class="tio-row justify-between mb-3">
          <Text @variant="muted" @size="sm">
            {{t "people.results_count" count=this.total}}
          </Text>
        </div>

        {{#if this.activeFilterChips.length}}
          <div class="tio-cluster-tight mb-3">
            {{#each this.activeFilterChips key="key" as |chip|}}
              <Chip @intent="brand" @appearance="faded" @size="sm">
                {{chip.label}}
                <Button
                  @appearance="minimal"
                  @size="sm"
                  aria-label={{t "people.remove_filter" label=chip.label}}
                  {{on "click" chip.remove}}
                >
                  <PhX @size={{12}} />
                </Button>
              </Chip>
            {{/each}}
          </div>
        {{/if}}

        {{#if (gt this.items.length 0)}}
          {{#if this.showNoExactNameNotice}}
            <AlertBanner @intent="info" @size="sm" class="mb-3">
              {{t "people.no_exact_matches_notice"}}
            </AlertBanner>
          {{/if}}
          <div class="grid grid-cols-1 gap-3">
            {{#each this.cards as |card|}}
              <PersonCard @person={{card}} @route={{DETAIL_ROUTE}} />
            {{/each}}
          </div>

          {{#if this.showPaginator}}
            <div class="mt-4">
              <Paginator
                @totalItems={{this.total}}
                @pageSize={{@controller.limit}}
                @currentPage={{@controller.page}}
                @onChange={{this.changePaginatorPage}}
                @summary={{true}}
              />
            </div>
          {{/if}}
        {{else}}
          <Section @headingLevel={{3}}>
            <:header>
              <div class="tio-row-center">
                <PhUsers @size="32" class="tio-fg-disabled mr-3" />
                <div>
                  {{t "people.no_results_found"}}
                  <Text @variant="muted" @size="sm">{{t "people.no_results_description"}}</Text>
                </div>
              </div>
            </:header>
          </Section>
        {{/if}}
      </div>
    </div>
  </template>
}
